import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { Loader } from 'theSource/components';
import { BeneficiarySelectCard } from '../BeneficiarySelectCard';
import { CreateNewBeneficiaryDrawer } from 'enrollment/features/AddYourBeneficiaries/components/CreateNewBeneficiaryDrawer';
import { PsButtonTuner } from 'stencilSetup';
import { addNewBeneficiary } from '../../services';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { replaceFirstSpaceWithDash } from 'enrollment/features/Benefits/BenefitsPreview/GroupedBenefits/utils';
import { generateRandomSixDigitNumber } from 'enrollment/utils/common/Utils';
import { CoverageLineDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { BeneficiaryDto } from 'theSource/queries';

export interface AddBeneficiariesDrawerProps {
  orgBenefitId: number;
  isLoading?: boolean;
  allocatedBeneficiaries?: BeneficiaryDto[];
  isPrimary: boolean;
  metadata: any;
  allBeneficiaries: any;
  hideAddToAllBenefits?: boolean;
  coverageLineId?: number;
}

export const AddBeneficiariesDrawer = ({
  orgBenefitId,
  isLoading,
  allocatedBeneficiaries,
  metadata,
  allBeneficiaries,
  isPrimary,
  hideAddToAllBenefits = false,
  coverageLineId,
}: AddBeneficiariesDrawerProps) => {
  const { t } = useTranslation('pages.enrollmentAddBeneficiaries');
  const theme = useTheme();
  const desktopViewConditions = useMediaQuery(theme.breakpoints.up('sm'));
  const [openAddBeneficiarDrawer, setAddBeneficiaryDrawer] = useState(false);
  const [checkboxStates, setCheckboxStates] = useState<Record<string, boolean>>(
    {}
  );
  const [inputAllocationField, setInputAllocationField] = useState<{
    [key: string]: string;
  }>({});
  const [addToAllBenefits, setAddToAllBenefits] = useState<
    Record<string, boolean>
  >({});

  const dispatch = useDispatch();

  const benefitPlansState = useSelector(
    (state: any) => state?.enrollment?.beneficiary?.beneficiaryData
  );

  const nonAllocatedBeneficiaries = allBeneficiaries?.filter(
    (beneficiary: BeneficiaryDto) => {
      return (
        allocatedBeneficiaries?.find(
          (allocatedBeneficiary: BeneficiaryDto) =>
            allocatedBeneficiary.name === beneficiary.name &&
            allocatedBeneficiary.relationship === beneficiary.relationship
        ) === undefined
      );
    }
  );

  const handleAddToAllBenefits = (beneficiary: BeneficiaryDto) => {
    benefitPlansState?.forEach((plan: any) => {
      const isBeneficiaryAlreadyAdded = plan.beneficiaries?.some(
        (existingBeneficiary: any) => {
          const isBeneficiaryAlreadyAddedCondition =
            existingBeneficiary?.name === beneficiary?.name &&
            existingBeneficiary?.relationship ===
              beneficiary?.relationship;

          return isBeneficiaryAlreadyAddedCondition;
        }
      );

      if (!isBeneficiaryAlreadyAdded) {
        if (plan.stackedCoverageLines.length > 0) {
          plan.stackedCoverageLines.forEach((line: CoverageLineDto) => {
            const lineBeneficiary = {
              ...beneficiary,
              coverage_line_id: line.id,
            };
            dispatch(
              addNewBeneficiary({
                orgBenefitId: plan?.orgBenefitId,
                beneficiaryAdded: lineBeneficiary,
              })
            );
          });
        } else {
          dispatch(
            addNewBeneficiary({
              orgBenefitId: plan?.orgBenefitId,
              beneficiaryAdded: beneficiary,
            })
          );
        }
      }
    });
  };

  const handleCloseAddBeneficiaryDrawer = () => {
    setAddBeneficiaryDrawer(false);
    setCheckboxStates({});
    setInputAllocationField({});
  };

  const handleSubmitAddBeneficiaryDrawer = () => {
    const hasCheckboxStates = Object?.values(checkboxStates)?.some(
      (value) => value === true
    );

    const hasInputAllocationField =
      hasCheckboxStates &&
      Object?.values(inputAllocationField)?.some((value) => value !== '');

    if (hasCheckboxStates && hasInputAllocationField) {
      nonAllocatedBeneficiaries?.forEach((beneficiary: BeneficiaryDto) => {
        const inputAllocationId = `allocation_${beneficiary?.name?.replace(
          ' ',
          '-'
        )}_${beneficiary?.relationship}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`;

        const formattedName = replaceFirstSpaceWithDash(beneficiary?.name);

        const addToAllBenefitsId = `add-to-all-benefit_${formattedName}_${beneficiary?.relationship}-${orgBenefitId}${coverageLineId ? '-' + coverageLineId : ''}`;

        if (
          inputAllocationField[inputAllocationId] !== '' &&
          inputAllocationField[inputAllocationId] !== undefined &&
          inputAllocationField[inputAllocationId] !== null
        ) {
          const beneficiaryAdded = {
            ...beneficiary,
            uniq_id: generateRandomSixDigitNumber(),
            allocation: inputAllocationField[inputAllocationId],
            primary_beneficiary: isPrimary,
            coverage_line_id: coverageLineId,
          };

          if (addToAllBenefits[addToAllBenefitsId]) {
            handleAddToAllBenefits(beneficiaryAdded);
          } else {
            dispatch(addNewBeneficiary({ orgBenefitId, beneficiaryAdded }));
          }
        }
      });
    }
    setAddBeneficiaryDrawer(false);
  };

  return (
    <>
      <PsButtonTuner
        buttonId={`add-beneficiary-btn-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
        label={
          desktopViewConditions ? t('addBeneficiaryButton') : t('common:add')
        }
        level="secondary"
        icon="plus"
        onClick={() => setAddBeneficiaryDrawer(true)}
        data-testid={`add-beneficiary-btn-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
      />
      <OrbitDrawer
        data-testid={`add-beneficiary-drawer-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
        drawerId={`add-beneficiary-drawer-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
        heading={t('pageTitleBeneficiaryDrawer')}
        isOpen={openAddBeneficiarDrawer ?? false}
        handleClose={handleCloseAddBeneficiaryDrawer}
        handleSubmit={handleSubmitAddBeneficiaryDrawer}
        footer={true}
        saveButton={t('common:save')}
      >
        <div>
          <p className="mb-4" style={{ color: '#55575E' }}>
            {t('pageDescriptionBeneficiaryDrawer')}
          </p>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {metadata && nonAllocatedBeneficiaries?.length > 0 ? (
                <>
                  <p>{t('existingBeneficiaries')}</p>
                  <>
                    {nonAllocatedBeneficiaries?.map(
                      (beneficiary: BeneficiaryDto) => {
                        return (
                          <>
                            <BeneficiarySelectCard
                              orgBenefitId={orgBenefitId}
                              beneficiary={beneficiary}
                              key={Math.random()}
                              isPrimary={isPrimary}
                              checkboxStates={checkboxStates}
                              setCheckboxStates={setCheckboxStates}
                              hideCheckBox={hideAddToAllBenefits}
                              inputAllocationField={inputAllocationField}
                              setInputAllocationField={setInputAllocationField}
                              addToAllBenefits={addToAllBenefits}
                              handleAddToAllBenefits={handleAddToAllBenefits}
                              setAddToAllBenefits={setAddToAllBenefits}
                              coverageLineId={coverageLineId}
                              metadata={metadata}
                            />
                            <div
                              tabIndex={0}
                              aria-hidden="true"
                              style={{ height: 0, overflow: 'hidden' }}
                              className="sr-only"
                              // I am an escape hatch so the user isn't keyboard locked in the BeneficiarySelectCard. Do not remove me.
                            />
                          </>
                        );
                      }
                    )}
                  </>
                </>
              ) : (
                <div>
                  <p>{t('existingBeneficiaries')}</p>
                  <p>{t('existingBeneficiariesDescription')}</p>
                </div>
              )}
              {metadata && (
                <div className="d-flex justify-content-center">
                  <CreateNewBeneficiaryDrawer
                    orgBenefitId={orgBenefitId}
                    metadata={metadata}
                    isPrimary={isPrimary}
                    setAddBeneficiaryDrawer={setAddBeneficiaryDrawer}
                    coverageLineId={coverageLineId}
                    handleAddToAllBenefits={handleAddToAllBenefits}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </OrbitDrawer>
      {/* Try moving the logic and drawer for 'CreateNewBeneficiaryDrawer' here */}
    </>
  );
};
